import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import { useSelector } from 'react-redux';
import CustomModal from '../../components/CustomComponents/modal/customModal';
import ServeIQLoader from '../../components/CustomComponents/preLoad/serveiqLoader';
import showErrorToast, { showSuccessToast } from '../../components/CustomComponents/toast/toast';
import useDarkMode from '../../hooks/useDarkMode';
import {
	buildOrderTree,
	getMenuItemName,
	getMenuItemQty,
	getMenuItemPrice,
	getMenuItemType,
	getSubTotal,
	getMenuItemIsModifier,
	isModifierLine,
	isModifierType,
	isVariantType,
	isDealType,
	isComboType,
	isDealComboSlotChild,
	isDealOrComboHeader,
	getNestedModifierOptionNodes,
	getModifierChildNodes,
	getRecvOrderDetailsList,
	normalizeType,
} from '../../utils/orderDetailsTree';
import './orderDetailView.scss';

function OrderDetailsView() {
	const { orderId } = useParams();
	const [searchParams] = useSearchParams();
	const isDarkMode = useDarkMode();
	const [orderData, setOrderData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [compCurrency, setCompCurrency] = useState('GBP');
	const [dealDetailsCache, setDealDetailsCache] = useState(new Map()); // Cache deal details by MenuItem_ID
	const fetchingDealsRef = React.useRef(new Set()); // Track deals currently being fetched

	// Get invoice number from URL parameters (either from params or query string)
	const invoiceNo = orderId || searchParams.get('invoiceNo');

	// Get company ID from Redux store
	const CompID = useSelector((state) => state.auth.user)?.userData?.compID;

	// Currency symbols mapping
	const currencySymbols = {
		GBP: '£',
		USD: '$',
		EUR: '€',
	};

	const getCurrencySymbol = (currencyCode) => {
		return currencySymbols[currencyCode] || currencyCode;
	};

	const isFullRefundStatus = (s) => {
		if (!s || typeof s !== 'string') return false;
		const n = s.trim().toLowerCase().replace(/[\s_-]/g, '');
		return n === 'fullrefund' || n === 'refund';
	};
	const isPartialRefundStatus = (s) => {
		if (!s || typeof s !== 'string') return false;
		const n = s.trim().toLowerCase().replace(/[\s_-]/g, '');
		return n === 'partialrefund';
	};
	const isRejectedStatus = (s) => {
		if (!s || typeof s !== 'string') return false;
		return s.trim().toLowerCase() === 'rejected';
	};

	const getRefundTotalFromAdjustments = (adjustments = []) =>
		(Array.isArray(adjustments) ? adjustments : [])
			.filter((a) => String(a?.type ?? a?.Type ?? '').toLowerCase() === 'refund')
			.reduce((sum, a) => sum + (Number(a?.amount ?? a?.Amount) || 0), 0);

	const cleanRefundReasonText = (raw) => {
		let text = String(raw ?? '').trim();
		if (!text) return '';
		text = text.replace(/^(ITEM|ORDER|PARTIAL)\s*refund\s*:\s*/i, '').trim();
		return text;
	};

	const getOrderDetailNameById = (details = [], detailId) => {
		if (detailId == null || detailId === '') return null;
		const detail = (Array.isArray(details) ? details : []).find(
			(d) =>
				String(d?.recvorderDetail_ID ?? d?.RecvorderDetail_ID ?? d?.recvorderDetailId ?? '') ===
				String(detailId)
		);
		if (!detail) return null;
		const name = getMenuItemName(detail);
		return name ? String(name).trim() : null;
	};

	const getRefundLinesForAdjustment = (adjustment, refundLines = [], details = []) => {
		const adjNumericId = adjustment?.iD ?? adjustment?.ID;
		const adjKey = adjustment?.adjustment_ID ?? adjustment?.Adjustment_ID;
		const matched = (Array.isArray(refundLines) ? refundLines : []).filter((rl) => {
			const link = String(rl?.recvorderAdjustment_ID ?? rl?.RecvorderAdjustment_ID ?? '');
			if (!link || link === '0') return false;
			return (
				(adjNumericId != null && adjNumericId !== '' && link === String(adjNumericId)) ||
				(adjKey && link === String(adjKey))
			);
		});

		return matched
			.map((rl) => {
				const detailId = rl?.recvorderDetail_ID ?? rl?.RecvorderDetail_ID;
				const qty = Number(rl?.refundQty ?? rl?.RefundQty) || 0;
				if (!detailId || qty <= 0) return null;
				const name = getOrderDetailNameById(details, detailId);
				return {
					detailId: String(detailId),
					name: name || 'Unknown item',
					qty,
				};
			})
			.filter(Boolean);
	};

	/** Prefer Source_Label, then Notes, from REFUND adjustments (OrderAdjustment). */
	const getRefundReasonsFromAdjustments = (
		adjustments = [],
		refundLines = [],
		details = []
	) =>
		(Array.isArray(adjustments) ? adjustments : [])
			.filter((a) => String(a?.type ?? a?.Type ?? '').toLowerCase() === 'refund')
			.map((a) => {
				const label = cleanRefundReasonText(a?.source_Label ?? a?.Source_Label);
				const notes = cleanRefundReasonText(a?.notes ?? a?.Notes);
				const reason = label || notes;
				const amount = Number(a?.amount ?? a?.Amount) || 0;
				const method = String(a?.paymentMethod ?? a?.PaymentMethod ?? '').trim();
				const createdAt = a?.createdAt ?? a?.CreatedAt ?? null;
				const lines = getRefundLinesForAdjustment(a, refundLines, details);
				return {
					reason: reason || 'No reason recorded',
					amount,
					method,
					createdAt,
					hasReason: Boolean(reason),
					lines,
					adjNumericId: a?.iD ?? a?.ID ?? null,
					adjKey: a?.adjustment_ID ?? a?.Adjustment_ID ?? null,
				};
			})
			.filter((r) => r.amount > 0 || r.hasReason || (r.lines && r.lines.length > 0));

	const resolveOrderOutcomeReason = (order, refundEntries = []) => {
		const withReason = (Array.isArray(refundEntries) ? refundEntries : []).filter((r) => r.hasReason);
		if (withReason.length === 1) return withReason[0].reason;
		if (withReason.length > 1) {
			return withReason.map((r) => r.reason).join(' · ');
		}
		return String(
			order.orderRejectorRefundReason ?? order.OrderRejectorRefundReason ?? ''
		).trim();
	};

	const getOrderOutcomeInfo = (order) => {
		if (!order) return null;

		const orderStatus = String(order.recvorder_status ?? order.Recvorder_status ?? '').trim();
		const paymentStatus = String(
			order.recvorder_Paymentrecvstatus ?? order.Recvorder_Paymentrecvstatus ?? ''
		)
			.trim()
			.toLowerCase();
		const isPaid = paymentStatus === 'completed';
		const adjustments = order.recvorderAdjustments ?? order.RecvorderAdjustments ?? [];
		const refundLines = order.recvorderRefundLines ?? order.RecvorderRefundLines ?? [];
		const details = getRecvOrderDetailsList(order);
		const refundEntries = getRefundReasonsFromAdjustments(adjustments, refundLines, details);
		const reason = resolveOrderOutcomeReason(order, refundEntries);
		const isRefund = isFullRefundStatus(orderStatus);
		const isPartialRefund = isPartialRefundStatus(orderStatus);
		const isCancelled = orderStatus.toLowerCase() === 'cancelled';
		const isRejected = isRejectedStatus(orderStatus);
		const hasRefundInfo =
			Boolean(reason) ||
			refundEntries.length > 0 ||
			order?.isRefunded ||
			order?.IsRefunded;
		const refundTotal = getRefundTotalFromAdjustments(adjustments);
		const orderTotal = Number(order.recvorder_Totalamount ?? order.Recvorder_Totalamount ?? 0) || 0;

		const showRefundedSection =
			isRefund || isPartialRefund || (hasRefundInfo && isPaid) || (isRejected && isPaid);

		if (showRefundedSection) {
			return {
				kind: isPartialRefund ? 'partial-refund' : isRejected ? 'rejected-refund' : 'refund',
				title: isPartialRefund
					? 'Partially refunded'
					: isRejected
						? 'Order rejected'
						: 'Order refunded',
				amountLabel: isPartialRefund
					? `Refunded so far ${formatCurrency(refundTotal)}`
					: `Refunded ${formatCurrency(refundTotal > 0 ? refundTotal : orderTotal)}`,
				reason: reason || null,
				refundEntries,
			};
		}

		if (isRejected) {
			return {
				kind: 'rejected',
				title: 'Order rejected',
				amountLabel: null,
				reason: reason || null,
				refundEntries: [],
			};
		}

		if (isCancelled) {
			return {
				kind: 'cancelled',
				title: 'Order cancelled',
				amountLabel: null,
				reason: reason || null,
				refundEntries: [],
			};
		}

		return null;
	};

	const renderOrderOutcomeBanner = (outcome) => {
		if (!outcome) return null;
		const entries =
			Array.isArray(outcome.refundEntries) && outcome.refundEntries.length > 0
				? outcome.refundEntries
				: outcome.reason
					? [{ reason: outcome.reason, amount: 0, method: '', lines: [], hasReason: true }]
					: [];

		return (
			<div className={`od-outcome-banner od-outcome-banner--${outcome.kind}`} role="status">
				<div className="od-outcome-banner__main">
					<div className="od-outcome-banner__icon" aria-hidden>
						<i className="fa fa-undo" />
					</div>
					<div className="od-outcome-banner__body">
						<div className="od-outcome-banner__header">
							<span className="od-outcome-banner__title">{outcome.title}</span>
							{outcome.amountLabel ? (
								<span className="od-outcome-banner__amount">{outcome.amountLabel}</span>
							) : null}
						</div>
						{entries.length > 0 ? (
							<ul className="od-outcome-banner__reasons">
								{entries.map((entry, idx) => (
									<li key={`${entry.reason}-${entry.amount}-${idx}`}>
										<div className="od-outcome-banner__reason-row">
											<span className="od-outcome-banner__reason-text">
												{entry.hasReason === false ? 'No reason recorded' : entry.reason}
											</span>
											{(entry.amount > 0 || entry.method) && (
												<span className="od-outcome-banner__reason-meta">
													{entry.amount > 0 ? formatCurrency(entry.amount) : null}
													{entry.amount > 0 && entry.method ? ' · ' : null}
													{entry.method ? formatRefundPaymentLabel(entry.method) : null}
												</span>
											)}
										</div>
										{Array.isArray(entry.lines) && entry.lines.length > 0 ? (
											<div className="od-outcome-banner__lines">
												<span className="od-outcome-banner__lines-label">Refunded items</span>
												<ul>
													{entry.lines.map((line) => (
														<li key={`${line.detailId}-${line.qty}`}>
															<span className="od-outcome-banner__line-name">{line.name}</span>
															<span className="od-outcome-banner__line-qty">× {line.qty}</span>
														</li>
													))}
												</ul>
											</div>
										) : null}
									</li>
								))}
							</ul>
						) : (
							<p className="od-outcome-banner__empty-reason">No refund reason recorded</p>
						)}
					</div>
				</div>
			</div>
		);
	};

	// Fetch company currency
	useEffect(() => {
		const fetchCompCurrency = async () => {
			try {
				const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
				const profileData = response.data.data;
				setCompCurrency(profileData?.compCurrency || 'GBP');
			} catch (err) {
				console.error('Failed to load company currency:', err);
			}
		};
		fetchCompCurrency();
	}, []);

	useEffect(() => {
		if (invoiceNo) {
			fetchOrderDetails();
		} else {
			setError('Invoice number is required');
			setLoading(false);
		}
	}, [invoiceNo]);

	const fetchOrderDetails = async () => {
		try {
			setLoading(true);
			setError(null);
			
			const response = await axiosInstance.get(APIs.GET_ORDER_BY_INVOICE_NO, {
				params: {
					InvoiceNo: invoiceNo,
					CompID: CompID || 0
				}
			});
			console.log(response.data.data);
			
console.log(response.data.data);
			if (response.data.status) {
				setOrderData(response.data.data);
				console.log(response.data.data);
			} else {
				setError(response.data.message || 'Failed to fetch order details');
			}
		} catch (err) {
			setError(err.response?.data?.message || 'An error occurred while fetching order details');
		} finally {
			setLoading(false);
		}
	};

	const formatCurrency = (amount) => {
		return `${getCurrencySymbol(compCurrency)}${parseFloat(amount).toFixed(2)}`;
	};

	const formatPercentage = (value) => {
		const num = Number(value);
		if (!Number.isFinite(num)) return '0.00';
		return num.toFixed(2);
	};

	const formatDateTime = (dateTime) => {
		if (dateTime == null || dateTime === '') return '';
		const parsed = new Date(dateTime);
		if (Number.isNaN(parsed.getTime())) return String(dateTime);
		return parsed.toLocaleString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
		});
	};

	// Fetch deal details by MenuItem_ID (deal's menu item ID)
	const fetchDealDetails = async (menuItemId) => {
		if (!menuItemId) return null;
		
		// Check if already being fetched
		if (fetchingDealsRef.current.has(menuItemId)) {
			return null;
		}
		
		// Check cache
		let cachedData = null;
		setDealDetailsCache(prev => {
			if (prev.has(menuItemId)) {
				cachedData = prev.get(menuItemId);
			}
			return prev;
		});
		
		if (cachedData) {
			return cachedData;
		}
		
		fetchingDealsRef.current.add(menuItemId);
		
		try {
			// Fetch all deals and find the one with matching MenuItem_ID
			const response = await axiosInstance.get(APIs.GET_ALL_DEALS, {
				params: { CompID: CompID || 0 }
			});
			
			if (response.data && response.data.data && Array.isArray(response.data.data)) {
				const deal = response.data.data.find(d => 
					d.menuItem_ID === menuItemId || 
					d.MenuItem_ID === menuItemId ||
					d.menuItemId === menuItemId ||
					d.MenuItemId === menuItemId
				);
				
				if (deal) {
					// Fetch full deal details with slots
					const dealId = deal.dealID || deal.DealID || deal.dealId || deal.DealId;
					const dealResponse = await axiosInstance.get(APIs.GET_DEAL_BY_ID, {
						params: {
							DealID: dealId,
							CompID: CompID || 0
						}
					});
					
					if (dealResponse.data && dealResponse.data.data) {
						const dealData = dealResponse.data.data;
						
						// Also fetch deal slots
						try {
							const slotsResponse = await axiosInstance.get(APIs.GET_DEAL_SLOTS_BY_DEAL_ID, {
								params: {
									DealID: dealId,
									CompID: CompID || 0
								}
							});
							
							if (slotsResponse.data && slotsResponse.data.data) {
								dealData.slots = slotsResponse.data.data;
								
								// Fetch slot items for each slot
								for (const slot of dealData.slots || []) {
									try {
										const slotItemsResponse = await axiosInstance.get(APIs.GET_DEAL_SLOT_ALLOWED_ITEMS, {
											params: {
												SlotID: slot.slotID || slot.SlotID || slot.slotId || slot.SlotId,
												CompID: CompID || 0
											}
										});
										
										if (slotItemsResponse.data && slotItemsResponse.data.data) {
											slot.items = slotItemsResponse.data.data;
										}
									} catch (err) {
										console.error('Error fetching slot items:', err);
									}
								}
							}
						} catch (err) {
							console.error('Error fetching deal slots:', err);
						}
						
						// Update cache
						setDealDetailsCache(prev => {
							const newCache = new Map(prev);
							newCache.set(menuItemId, dealData);
							return newCache;
						});
						
						fetchingDealsRef.current.delete(menuItemId);
						return dealData;
					}
				}
			}
		} catch (err) {
			console.error('Error fetching deal details:', err);
			fetchingDealsRef.current.delete(menuItemId);
		}
		
		return null;
	};

	// Calculate slot-level price for a deal child item
	const calculateDealChildItemPrice = (childItem, dealData) => {
		if (!dealData || !dealData.slots || !Array.isArray(dealData.slots)) {
			return 0;
		}
		
		const childMenuItemId = childItem?.menuItem_ID ?? childItem?.MenuItem_ID ?? childItem?.menuItemId ?? childItem?.MenuItemId;
		if (!childMenuItemId) {
			return 0;
		}
		
		// Find which slot this item belongs to
		for (const slot of dealData.slots) {
			if (!slot.items || !Array.isArray(slot.items)) continue;
			
			// Try multiple field name variations for matching
			const slotItem = slot.items.find(item => {
				const itemId = item.itemID || item.ItemID || item.itemId || item.ItemId || 
				              item.menuItem_ID || item.MenuItem_ID || item.menuItemId || item.MenuItemId ||
				              item.menuItemID || item.MenuItemID;
				return itemId === childMenuItemId;
			});
			
			if (slotItem) {
				// Get item price - try multiple field names
				const itemPrice = parseFloat(
					slotItem.price || 
					slotItem.Price || 
					slotItem.itemPrice || 
					slotItem.ItemPrice ||
					slotItem.MenuItem_Price ||
					slotItem.menuItem_Price ||
					slotItem.MenuItem_SalePrice ||
					slotItem.menuItem_SalePrice ||
					0
				);
				
				if (itemPrice <= 0) continue;
				
				// Get slot-level pricing
				const slotPricingType = (slot.pricingType || slot.PricingType || "").trim();
				const isSlotFree = slot.isFree || slot.IsFree || false;
				const slotDiscountPercentage = slot.discountPercentage ?? slot.DiscountPercentage ?? 0;
				const minimumOrderValue = dealData.minimumOrderValue || dealData.MinimumOrderValue || 0;
				
				// Apply slot-level pricing when minimumOrderValue is set
				if (minimumOrderValue > 0) {
					if (slotPricingType === "Free" || isSlotFree) {
						return 0;
					}
					if (slotPricingType === "HalfPrice") {
						return itemPrice * 0.5;
					}
					if ((slotPricingType === "Discount" || slotPricingType === "CustomDiscount") && slotDiscountPercentage > 0) {
						return itemPrice - (itemPrice * slotDiscountPercentage / 100);
					}
					// Fixed or default - use full price
					return itemPrice;
				}
				
				// If no minimumOrderValue, check deal-level pricing
				const dealPricingType = (dealData.dealPricingType || "").trim().toLowerCase();
				const dealDiscountPercentage = dealData.dealDiscountPercentage || 0;
				
				if ((dealPricingType === "customdiscount" || dealPricingType === "discount") && dealDiscountPercentage > 0) {
					return itemPrice - (itemPrice * dealDiscountPercentage / 100);
				}
				
				// Default: return full price
				return itemPrice;
			}
		}
		
		return 0;
	};

	// Helper to calculate display price for deal child items
	// This function is recreated on each render to access the latest dealDetailsCache state
	const getDealChildItemDisplayPrice = React.useCallback((item, parentDealItem = null) => {
		const currentPrice = getMenuItemPrice(item);
		
		// If current price is > 0, use it (backend stored the price)
		if (currentPrice > 0) {
			return currentPrice;
		}
		
		// If we have parent deal item, try to get deal data from cache
		if (parentDealItem) {
			const dealMenuItemId = parentDealItem?.menuItem_ID ?? parentDealItem?.MenuItem_ID ?? parentDealItem?.menuItemId ?? parentDealItem?.MenuItemId;
			
			if (dealMenuItemId && dealDetailsCache.has(dealMenuItemId)) {
				const dealData = dealDetailsCache.get(dealMenuItemId);
				const calculatedPrice = calculateDealChildItemPrice(item, dealData);
				if (calculatedPrice > 0) {
					return calculatedPrice;
				}
			}
		}
		
		// Last resort: return 0 (item is free/included, or we don't have pricing data)
		return 0;
	}, [dealDetailsCache]);

	/**
	 * Price a slot pick actually contributed to the deal: its catalogue price less the deal
	 * discount stored on the line (e.g. a £7.00 pizza that "3rd half price" brought to £3.50).
	 * Falls back to reconstructing from the deal definition for older rows that stored no price.
	 */
	const getDealChildPaidUnitPrice = React.useCallback((item, parentDealItem = null) => {
		const catalogUnit = getMenuItemPrice(item);
		if (catalogUnit > 0) {
			const qty = getMenuItemQty(item) || 1;
			const discountPerUnit = (parseFloat(item?.discountAmount ?? item?.DiscountAmount ?? 0) || 0) / qty;
			return Math.max(0, catalogUnit - discountPerUnit);
		}
		return getDealChildItemDisplayPrice(item, parentDealItem);
	}, [getDealChildItemDisplayPrice]);

	// Helper to calculate display subtotal for deal child items (optionally scaled by parent deal quantity, like modifiers)
	const getDealChildItemDisplaySubtotal = React.useCallback((item, parentDealItem = null) => {
		const displayPrice = getDealChildPaidUnitPrice(item, parentDealItem);
		const qty = getMenuItemQty(item);
		const parentDealQty = parentDealItem ? (getMenuItemQty(parentDealItem) || 1) : 1;
		return displayPrice * qty * parentDealQty;
	}, [getDealChildPaidUnitPrice]);

	// Helper to recursively calculate total for a node (including all nested children and modifiers)
	const calculateNodeTotal = React.useCallback((node, parentDealItem = null) => {
		if (!node || !node.item) {
			return 0;
		}
		
		let total = 0;
		const parentDealQty = parentDealItem ? (getMenuItemQty(parentDealItem) || 1) : 1;
		const item = node.item;
		
		// If this is a modifier, calculate its price
		if (isModifierType(item)) {
			const modPrice = getMenuItemPrice(item);
			const modQty = getMenuItemQty(item);
			const effectiveModQty = modQty * parentDealQty;
			return modPrice * effectiveModQty;
		}
		
		// If this is a variant, skip it (variants don't have separate prices)
		if (isVariantType(item)) {
			return 0;
		}
		
		// If this is a child item (not a modifier/variant), calculate its price
		if (parentDealItem) {
			total += getDealChildItemDisplaySubtotal(item, parentDealItem);
		}
		
		// Recursively sum all children (including nested modifiers)
		if (node.children && Array.isArray(node.children)) {
			node.children.forEach((child) => {
				total += calculateNodeTotal(child, parentDealItem);
			});
		}
		
		return total;
	}, [getDealChildItemDisplaySubtotal]);

	// Helper to calculate deal header total from all its children (items + modifiers)
	const calculateDealHeaderTotal = React.useCallback((node, parentDealItem = null) => {
		if (!node || !node.children || !Array.isArray(node.children)) {
			return 0;
		}
		
		let total = 0;
		
		// Sum all children recursively (this will include child items and all their nested modifiers)
		node.children.forEach((child) => {
			total += calculateNodeTotal(child, parentDealItem);
		});
		
		return total;
	}, [calculateNodeTotal]);

	// Order details array (API may return orderDetails or OrderDetails)
	const orderDetailsList = getRecvOrderDetailsList(orderData);

	const adjustmentsList = orderData?.recvorderAdjustments ?? orderData?.RecvorderAdjustments ?? [];

	const showPromoCodeRow = React.useMemo(() => {
		const promoCode = String(orderData?.recvorder_promotioncode ?? '').trim();
		const promoDiscountAmount = Number(orderData?.recvorder_promotionDiscountAmount) || 0;
		if (!promoCode || promoDiscountAmount <= 0) return false;
		const isLoyaltyPromo = (Array.isArray(adjustmentsList) ? adjustmentsList : []).some((a) => {
			if (String(a?.type ?? a?.Type ?? '').toUpperCase() !== 'LOYALTY') return false;
			return String(a?.source_ID ?? a?.Source_ID ?? '') === promoCode;
		});
		return !isLoyaltyPromo;
	}, [orderData?.recvorder_promotioncode, orderData?.recvorder_promotionDiscountAmount, adjustmentsList]);

	const rootRefundLinesList =
		orderData?.recvorderRefundLines ?? orderData?.RecvorderRefundLines ?? [];

	const refundedQtyByDetailId = React.useMemo(() => {
		const map = {};
		if (!Array.isArray(rootRefundLinesList)) return map;
		rootRefundLinesList.forEach((rl) => {
			const id = rl.recvorderDetail_ID ?? rl.RecvorderDetail_ID;
			const q = Number(rl.refundQty ?? rl.RefundQty ?? 0);
			if (!id) return;
			map[id] = (map[id] || 0) + q;
		});
		return map;
	}, [rootRefundLinesList]);

	const [partialRefundOpen, setPartialRefundOpen] = useState(false);
	const [partialRefundSubmitting, setPartialRefundSubmitting] = useState(false);
	const [refundSelections, setRefundSelections] = useState({});
	const [refundNotes, setRefundNotes] = useState('');
	const [refundPaymentMethod, setRefundPaymentMethod] = useState('');

	const REFUND_PAYMENT_OPTIONS = [
		{ value: 'cash', label: 'Cash' },
		{ value: 'card', label: 'Card' },
		{ value: 'online', label: 'Online' },
		{ value: 'deposit', label: 'Deposit' },
	];

	const getDisplayPaymentMethod = (orderLike) => {
		const rawMethod = String(
			orderLike?.recvorder_Paymentmethod ??
			orderLike?.Recvorder_Paymentmethod ??
			orderLike?.paymentmethod ??
			orderLike?.Paymentmethod ??
			''
		).trim();
		const rawStatus = String(
			orderLike?.recvorder_Paymentrecvstatus ??
			orderLike?.Recvorder_Paymentrecvstatus ??
			orderData?.recvorder_Paymentrecvstatus ??
			''
		).trim().toLowerCase();
		const rawPaymentBy = String(
			orderLike?.recvorder_PaymentBy ??
			orderLike?.Recvorder_PaymentBy ??
			orderData?.recvorder_PaymentBy ??
			''
		).trim().toLowerCase();

		const gatewayRef = String(
			orderLike?.cr_ReferenceNo ??
			orderLike?.Cr_ReferenceNo ??
			''
		).trim().toLowerCase();
		const isDojoCardPayment =
			rawMethod.toLowerCase() === 'online' &&
			(rawStatus === 'completed' ||
				String(orderData?.recvorder_Paymentrecvstatus ?? '').trim().toLowerCase() === 'completed') &&
			(rawPaymentBy === 'dojo' ||
				String(orderData?.recvorder_PaymentBy ?? '').trim().toLowerCase() === 'dojo' ||
				gatewayRef.startsWith('pi_'));

		if (isDojoCardPayment) {
			return 'Card';
		}
		return rawMethod;
	};

	const inferDefaultRefundPaymentMethod = (order) => {
		const pm = String(getDisplayPaymentMethod(order) || '').trim().toLowerCase();
		if (pm === 'cash') return 'cash';
		if (pm === 'card') return 'card';
		if (pm === 'online' || pm === 'onlinedeposit' || pm === 'online deposit' || pm === 'online_deposit') return 'online';
		if (pm === 'deposit') return 'deposit';
		return '';
	};

	const formatRefundPaymentLabel = (method) => {
		const m = String(method ?? '').trim().toLowerCase();
		const opt = REFUND_PAYMENT_OPTIONS.find((o) => o.value === m);
		return opt?.label ?? (method || '—');
	};

	// Fetch deal details for all deals in the order
	useEffect(() => {
		if (!orderData || !orderDetailsList.length) return;

		const fetchDealsForOrder = async () => {
			// Find all unique deal MenuItem_IDs in the order
			const dealMenuItemIds = new Set();

			orderDetailsList.forEach((item) => {
				const type = String(item?.menuItem_Type ?? item?.MenuItem_Type ?? '').trim().toUpperCase();
				if (type.includes('DEAL')) {
					const dealMenuItemId = item?.menuItem_ID ?? item?.MenuItem_ID ?? item?.menuItemId ?? item?.MenuItemId;
					if (dealMenuItemId) {
						dealMenuItemIds.add(dealMenuItemId);
					}
				}
			});
			
			// Fetch deal details for each unique deal
			for (const dealMenuItemId of dealMenuItemIds) {
				await fetchDealDetails(dealMenuItemId);
			}
		};
		
		fetchDealsForOrder();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderData, CompID]);

	const getInstructions = (item) => {
		return item?.recvorder_SInstructions ?? item?.Recvorder_SInstructions ?? item?.recvorderSInstructions ?? '';
	};

	const isRefundableDetailRow = (item) => {
		const t = normalizeType(getMenuItemType(item));
		if (t === 'MODIFIER' || t === 'VARIANT' || t === 'DISCOUNT') return false;
		// Deal/combo slot picks carry no money of their own — the package price sits on the
		// header row — so refunding them would take the same food off the bill twice.
		if (isDealComboSlotChild(item)) return false;
		return true;
	};

	const getMainLineCount = (items = []) => (items || []).filter((it) => !isModifierLine(it)).length;
	
	// Count sellable packages as shown to the customer (1 deal/combo = 1 item, not each slot pick)
	const getActualItemCount = (items = []) => {
		if (!items || !Array.isArray(items)) return 0;
		const roots = buildOrderTree(items);
		let count = 0;
		const countNode = (node) => {
			if (!node?.item) return;
			const it = node.item;
			if (isDealOrComboHeader(it)) {
				// One deal/combo package counts as a single item (matches cart / checkout).
				count += 1;
				return;
			}
			if (!isModifierType(it) && !isVariantType(it)) count += 1;
		};
		roots.forEach(countNode);
		return count;
	};

	const getPaymentDisplayCharges = (list = [], data = null) => {
		const payableSubtotal = getSumOfItemSubtotals(list) || 0;
		const storedService = Number(data?.recvorder_Servicecharges ?? data?.Recvorder_Servicecharges ?? 0) || 0;
		const servicePct = Number(data?.recvorder_Servicechargespercentage ?? data?.Recvorder_Servicechargespercentage ?? 0) || 0;
		const storedVat = Number(data?.vatAmount ?? data?.VATAmount ?? data?.VatAmount ?? 0) || 0;
		const vatPct = Number(data?.recvorder_VATpercentage ?? data?.Recvorder_VATpercentage ?? 0) || 0;
		const vatType = String(data?.recvorder_VATType ?? data?.Recvorder_VATType ?? '').toUpperCase();
		const vatInclusive = vatType === 'INCLUSIVE';
		const discount = Number(data?.recvorder_Totaldiscount ?? data?.Recvorder_Totaldiscount ?? 0) || 0;
		const tip = Number(data?.recvorder_TipAmount ?? data?.Recvorder_TipAmount ?? 0) || 0;
		const delivery = Number(data?.recvorder_Deliverycharges ?? data?.Recvorder_Deliverycharges ?? 0) || 0;

		// When deal children incorrectly carried SubTotals historically, stored service/VAT
		// were often ~2x the payable package total. Prefer percentage × payable subtotal.
		let displayService = storedService;
		if (servicePct > 0 && payableSubtotal >= 0) {
			const expectedService = (payableSubtotal * servicePct) / 100;
			if (storedService > expectedService * 1.05 + 0.01) {
				displayService = expectedService;
			}
		}

		let displayVat = storedVat;
		if (vatPct > 0 && payableSubtotal >= 0) {
			const baseForVat = vatInclusive
				? Math.max(0, payableSubtotal + displayService + delivery + tip - discount)
				: Math.max(0, payableSubtotal);
			const expectedVat = vatInclusive
				? baseForVat * (vatPct / (100 + vatPct))
				: baseForVat * (vatPct / 100);
			if (storedVat > expectedVat * 1.05 + 0.01) {
				displayVat = expectedVat;
			}
		}

		return {
			payableSubtotal,
			displayService,
			displayVat,
		};
	};

	// Payment rollup: modifier option lines only (nested groups sum their options).
	const sumModifierPaymentSubtotal = (modNode) => {
		const nested = getNestedModifierOptionNodes(modNode);
		if (nested.length > 0) {
			return nested.reduce((sum, child) => sum + sumModifierPaymentSubtotal(child), 0);
		}
		const modSubtotal = parseFloat(getSubTotal(modNode?.item)) || 0;
		return modSubtotal > 0 ? modSubtotal : 0;
	};

	/**
	 * Add-on charges billed on their own rows beneath a line, at any depth.
	 * Plain products keep the charge there. Inside a deal/combo package the POS till and current
	 * online orders fold it into the header, leaving these rows at 0, so this returns 0 for them;
	 * older online orders kept it on the modifier row and this recovers it.
	 */
	const sumChildModifierSubtotals = (node) => {
		if (!node?.children?.length) return 0;
		return node.children.reduce((sum, child) => {
			if (isVariantType(child.item)) return sum;
			if (isModifierLine(child.item)) return sum + sumModifierPaymentSubtotal(child);
			return sum + sumChildModifierSubtotals(child);
		}, 0);
	};

	// Payment rollup for a product line and its modifiers (not deal/combo headers).
	const sumProductNodePaymentSubtotal = (node) => {
		if (!node?.item) return 0;
		const it = node.item;
		if (isModifierLine(it) || isVariantType(it)) return 0;

		const storedSubtotal = parseFloat(getSubTotal(it)) || 0;
		const lineTotal =
			storedSubtotal > 0 ? storedSubtotal : getMenuItemPrice(it) * getMenuItemQty(it);

		let total = lineTotal;
		getModifierChildNodes(node)
			.filter((c) => !isVariantType(c.item))
			.forEach((m) => {
				total += sumModifierPaymentSubtotal(m);
			});

		(node.children || [])
			.filter((c) => !isModifierLine(c.item) && !isVariantType(c.item))
			.forEach((child) => {
				total += sumProductNodePaymentSubtotal(child);
			});

		return total;
	};

	/**
	 * Payment Records subtotal per root line.
	 * Deal/combo: use header SubTotal only when set (online orders); otherwise sum slot lines.
	 * Avoids double-counting slot items + modifiers already included in the deal price.
	 */
	const getPaymentRootSubtotal = (node) => {
		if (!node?.item) return 0;

		const it = node.item;
		const isThisDealOrCombo = isDealType(it) || isComboType(it);
		const storedSubtotal = parseFloat(getSubTotal(it)) || 0;
		const lineTotal =
			storedSubtotal > 0 ? storedSubtotal : getMenuItemPrice(it) * getMenuItemQty(it);
		const slotChildren = (node.children || []).filter((c) => !isModifierLine(c.item));

		if (isThisDealOrCombo) {
			if (lineTotal > 0) return lineTotal + sumChildModifierSubtotals(node);
			if (slotChildren.length > 0) {
				return slotChildren.reduce((sum, c) => sum + sumProductNodePaymentSubtotal(c), 0);
			}
			return lineTotal;
		}

		return sumProductNodePaymentSubtotal(node);
	};

	const getSumOfItemSubtotals = (list = []) => {
		if (!list || list.length === 0) return 0;
		const roots = buildOrderTree(list);
		return roots.reduce((sum, root) => sum + getPaymentRootSubtotal(root), 0);
	};

	/**
	 * Add-on charges exactly as the modifier rows beneath a line print them, so a line total that
	 * includes its add-ons always matches the rows a reader can see. Display only: deal add-ons
	 * carry no SubTotal, and payment rollups keep reading SubTotal.
	 */
	const sumModifierDisplayAmounts = (node, parentDealQty = 1) => {
		if (!node?.children?.length) return 0;
		return node.children.reduce((sum, child) => {
			const mi = child.item;
			if (isVariantType(mi) || !isModifierLine(mi)) return sum;
			if (getNestedModifierOptionNodes(child).length > 0) {
				return sum + sumModifierDisplayAmounts(child, parentDealQty);
			}
			const modSubtotal = parseFloat(getSubTotal(mi)) || 0;
			if (modSubtotal > 0) return sum + modSubtotal;
			const modDiscount = parseFloat(mi?.discountAmount ?? mi?.DiscountAmount ?? 0) || 0;
			const charged = Math.max(0, getMenuItemPrice(mi) * getMenuItemQty(mi) - modDiscount);
			return sum + charged * parentDealQty;
		}, 0);
	};

	const renderModifierNode = (modNode, depth, isChildItem, currentParentDealItem) => {
		const mi = modNode?.item || {};
		const nestedOptions = getNestedModifierOptionNodes(modNode);
		const isGroupHeader = nestedOptions.length > 0;
		const modQty = getMenuItemQty(mi);
		const modName = getMenuItemName(mi);
		const modSubtotal = parseFloat(getSubTotal(mi)) || 0;
		const parentDealQty =
			isChildItem && currentParentDealItem ? getMenuItemQty(currentParentDealItem) || 1 : 1;
		const effectiveModQty = isChildItem && currentParentDealItem ? modQty * parentDealQty : modQty;
		// Deal modifiers carry no SubTotal (their cost is inside the deal header), so fall back to
		// the line's own charged amount. Display only — payment rollups still read SubTotal.
		const modLineDiscount = parseFloat(mi?.discountAmount ?? mi?.DiscountAmount ?? 0) || 0;
		const modChargedLineTotal = Math.max(0, getMenuItemPrice(mi) * modQty - modLineDiscount);
		const modDisplayAmount = modSubtotal > 0 ? modSubtotal : modChargedLineTotal * parentDealQty;
		const rowKey = modNode.id ?? mi?.recvorderDetail_ID ?? mi?.RecvorderDetail_ID ?? modName;

		if (isGroupHeader) {
			return (
				<div key={rowKey} className="od-mod-group">
					<div className="od-mod-group-title">{modName}</div>
					<div className="od-mod-group-options">
						{nestedOptions.map((opt) =>
							renderModifierNode(opt, depth + 1, isChildItem, currentParentDealItem)
						)}
					</div>
				</div>
			);
		}

		const showAsOption = getMenuItemIsModifier(mi) || normalizeType(getMenuItemType(mi)) === 'MODIFIER';
		return (
			<div key={rowKey} className="od-mod-row">
				<span className="od-mod-qty">{effectiveModQty}×</span>
				<span className="od-mod-name">
					{showAsOption ? `+ ${modName}` : modName}
				</span>
				{modDisplayAmount > 0 && (
					<span className="od-mod-price">{formatCurrency(modDisplayAmount)}</span>
				)}
			</div>
		);
	};

	const renderOrderNode = (node, depth = 0, isChildItem = false, parentDealItem = null) => {
		const it = node.item || {};
		const type = normalizeType(getMenuItemType(it));
		const isThisDeal = isDealType(it);
		const isThisCombo = isComboType(it);
		const isThisDealOrCombo = isThisDeal || isThisCombo;
		// If this is a deal/combo, it becomes the parent for its children
		const currentParentDealItem = isThisDealOrCombo ? it : parentDealItem;
		
		// Calculate deal header total from children if stored subtotal is 0 (for dynamic deals)
		const storedSubtotal = parseFloat(getSubTotal(it)) || 0;
		const headerPriceTotal = getMenuItemPrice(it) * getMenuItemQty(it);
		const shouldCalculateFromChildren = isThisDealOrCombo && storedSubtotal === 0 && node.children && node.children.length > 0;
		const dealHeaderTotal = shouldCalculateFromChildren
			? calculateDealHeaderTotal(node, currentParentDealItem)
			: storedSubtotal;
		const effectiveHeaderSubtotal =
			storedSubtotal > 0
				? storedSubtotal
				: shouldCalculateFromChildren && dealHeaderTotal > 0
					? dealHeaderTotal
					: headerPriceTotal;
		// Subtotal shown against a line covers what the customer paid for it, add-ons included,
		// so it reconciles with Payment summary. Deals whose header total is derived from the
		// child rows already count modifiers; only online deals with a stored header need the
		// top-up, for older orders that billed deal modifiers on their own rows.
		const linePackageTotal =
			effectiveHeaderSubtotal +
			(isThisDealOrCombo
				? storedSubtotal > 0
					? sumChildModifierSubtotals(node)
					: 0
				: sumChildModifierSubtotals(node));

		// Split children into: dealItems/other mains, variants, modifiers (with nested option lines)
		const variants = node.children.filter((c) => isVariantType(c.item));
		const modifiers = getModifierChildNodes(node).filter((c) => !isVariantType(c.item));
		let otherChildren = node.children.filter((c) => !isModifierLine(c.item));
		if (isThisDealOrCombo && otherChildren.length > 0) {
			otherChildren = [...otherChildren].sort((a, b) => {
				const priceA = getDealChildPaidUnitPrice(a.item, currentParentDealItem);
				const priceB = getDealChildPaidUnitPrice(b.item, currentParentDealItem);
				// Free items (price = 0) should come last
				if (priceA === 0 && priceB > 0) return 1;
				if (priceB === 0 && priceA > 0) return -1;
				return 0; // Keep original order for items with same free/paid status
			});
		}

		const indentStyle = { marginLeft: depth * 15 };

		const icon = (() => {
			if (type === 'MODIFIER') return 'fa-plus';
			if (type === 'VARIANT') return 'fa-tag';
			if (isThisDeal) return 'fa-gift';
			if (isThisCombo) return 'fa-gift';
			if (isChildItem) return 'fa-check-circle'; // Deal/Combo item indicator
			return 'fa-utensils';
		})();

		const showQtyBadge = !isModifierType(it);
		// For deal/combo children, effective qty = child qty × parent deal qty (like modifiers under an item with qty 5)
		const parentDealQty = (isChildItem && currentParentDealItem) ? (getMenuItemQty(currentParentDealItem) || 1) : 1;
		const effectiveQty = isChildItem && currentParentDealItem ? (getMenuItemQty(it) || 1) * parentDealQty : getMenuItemQty(it);
		let displayName = getMenuItemName(it);

		// Show variant inline with parent item, e.g. "Chicken Tikka Pizza (Large)"
		if (variants.length > 0) {
			const variantMatch = displayName.match(/^(.+?)\s*\(([^)]+)\)$/);
			if (variantMatch) {
				displayName = variantMatch[1].trim();
			}
			const variantLabel = variants
				.map((v) => getMenuItemName(v.item))
				.filter((name) => name && name !== 'Unnamed')
				.join(', ');
			if (variantLabel) {
				displayName = `${displayName} (${variantLabel})`;
			}
		}

		// Determine badge text for deals vs combos
		const getBadgeText = () => {
			if (isThisDeal) return 'Combo';
			if (isThisCombo) return 'Deal';
			return null;
		};

		// Determine child items label
		const getChildItemsLabel = () => {
			if (isThisDeal) return 'Combo Items';
			if (isThisCombo) return 'Deal Items';
			return 'Items';
		};

		const priceCols = (() => {
			if (!isChildItem && (!isThisDealOrCombo || linePackageTotal > 0)) {
				const addOnsTotal = linePackageTotal - effectiveHeaderSubtotal;
				return (
					<>
						<div className="od-item-price flex-shrink-0">
							<div className="od-item-price-label">Unit Price</div>
							<div className="od-item-price-value">
								{isModifierType(it)
									? (getMenuItemPrice(it) > 0 ? formatCurrency(getMenuItemPrice(it)) : formatCurrency(0))
									: isThisDealOrCombo
										? `${formatCurrency(linePackageTotal / (getMenuItemQty(it) || 1))} x ${getMenuItemQty(it)}`
										: `${formatCurrency(getMenuItemPrice(it))} x ${getMenuItemQty(it)}`}
							</div>
							{!isThisDealOrCombo && addOnsTotal > 0.001 && (
								<div className="od-item-price-addons">
									+ {formatCurrency(addOnsTotal)} add-ons
								</div>
							)}
						</div>
						<div className="od-item-subtotal flex-shrink-0 text-end">
							<div className="od-item-subtotal-label">Subtotal</div>
							<div className="od-item-subtotal-value">
								{formatCurrency(linePackageTotal)}
							</div>
						</div>
					</>
				);
			}
			if (isChildItem && parentDealItem) {
				const storedSub = parseFloat(getSubTotal(it)) || 0;
				const childQty = getMenuItemQty(it) || 1;
				const paidUnit = getDealChildPaidUnitPrice(it, parentDealItem);
				const paidSubtotal =
					storedSub > 0 ? storedSub : paidUnit * childQty * (getMenuItemQty(parentDealItem) || 1);
				// A slot pick contributed its own (post-deal) price plus its add-ons, so show that
				// as the line total: the picks then add up to the deal price on the header.
				const addOnsTotal = sumModifierDisplayAmounts(node, parentDealQty);
				const slotLineTotal = paidSubtotal + addOnsTotal;
				return (
					<>
						<div className="od-item-price flex-shrink-0">
							<div className="od-item-price-label">Unit Price</div>
							<div className="od-item-price-value">
								{paidUnit > 0 ? formatCurrency(paidUnit) : 'Free'}
							</div>
							{addOnsTotal > 0.001 && (
								<div className="od-item-price-addons">
									+ {formatCurrency(addOnsTotal)} add-ons
								</div>
							)}
						</div>
						<div className="od-item-subtotal flex-shrink-0 text-end">
							<div className="od-item-subtotal-label">Subtotal</div>
							<div className="od-item-subtotal-value">
								{slotLineTotal > 0 ? formatCurrency(slotLineTotal) : 'Free'}
							</div>
						</div>
					</>
				);
			}
			return null;
		})();

		return (
			<div key={node.id ?? `${displayName}-${depth}`} className={`${isThisDealOrCombo ? 'mb-2' : isChildItem ? 'mb-1' : 'mb-2'}`}>
				{/* Main item row — flex (not Bootstrap row) so prices stay inside the card */}
				<div
					className={`od-item-row ${isThisDealOrCombo ? 'od-item-row--deal' : ''}`}
					style={indentStyle}
				>
					<div className="d-flex align-items-center flex-grow-1 min-w-0">
						<div className={`od-item-icon ${isChildItem || isThisDealOrCombo ? 'od-item-icon--accent' : ''}`}>
							<i className={`fa ${icon}`} aria-hidden="true" />
							{showQtyBadge && (
								<span className="od-item-qty">
									{isChildItem && currentParentDealItem ? effectiveQty : getMenuItemQty(it)}
								</span>
							)}
						</div>
						<div className="ps-2 min-w-0">
							<div className="d-flex align-items-center flex-wrap gap-1 mb-0">
								<span className="od-item-name text-truncate">
									{displayName}
								</span>
								{getBadgeText() && (
									<span className="badge od-accent-badge px-1 py-0" style={{ fontSize: '10px', lineHeight: '1.4' }}>
										<i className="fa fa-gift me-1" style={{ fontSize: '8px' }}></i>
										{getBadgeText()}
									</span>
								)}
								{isChildItem && (
									<span className="badge od-accent-badge px-1 py-0" style={{ fontSize: '9px', lineHeight: '1.4' }}>
										<i className="fa fa-check-circle me-1" style={{ fontSize: '8px' }}></i>
										Included
									</span>
								)}
							</div>
							{getInstructions(it) && (
								<div className="od-item-note">
									<i className="fa fa-sticky-note od-accent" aria-hidden="true" />
									<span>{getInstructions(it)}</span>
								</div>
							)}
						</div>
					</div>
					{priceCols}
				</div>

				{/* Render nested deal/combo items / child products */}
				{otherChildren.length > 0 && (
					<div className={`mt-2 ${isThisDealOrCombo ? 'ms-3 ps-3 border-start border-2 od-accent-border rounded-end' : ''}`} style={{ borderLeftWidth: '2px' }}>
						{isThisDealOrCombo && (
							<div className="d-flex align-items-center mb-1 mt-1">
								<div className="od-accent-bg rounded-pill px-2 py-0 d-inline-flex align-items-center" style={{ lineHeight: '1.5' }}>
									<i className="fa fa-list me-1 od-accent" style={{ fontSize: '10px' }}></i>
									<span className="od-accent fw-bold" style={{ fontSize: '11px' }}>
										{getChildItemsLabel()} ({otherChildren.length})
									</span>
								</div>
							</div>
						)}
						<div className={isThisDealOrCombo ? 'ps-1' : ''}>
							{otherChildren.map((child) => {
								return (
									<div key={child.id ?? `${getMenuItemName(child.item)}-${depth + 1}`}>
										{renderOrderNode(child, depth + 1, isThisDealOrCombo, currentParentDealItem)}
										{/* Variants are now shown in the item name itself (e.g., "Pizza Margherita (Large)") */}
									</div>
								);
							})}
						</div>
					</div>
				)}

				{/* Render Add-ons (modifiers) - improved design */}
				{modifiers.length > 0 && (
					<div className="od-mods" style={{ marginLeft: depth * 15 + 56 }}>
						{modifiers.map((m) =>
							renderModifierNode(m, depth, isChildItem, currentParentDealItem)
						)}
					</div>
				)}
			</div>
		);
	};

	const getDetailPk = (item) =>
		item?.recvorderDetail_ID ?? item?.RecvorderDetail_ID ?? item?.recvorderDetailId ?? null;

	const lineRefundCap = (item) => {
		const id = getDetailPk(item);
		if (!id) return 0;
		const ordered = Number(getMenuItemQty(item)) || 0;
		const already = refundedQtyByDetailId[id] || 0;
		return Math.max(0, ordered - already);
	};

	const canPartialRefund = () => {
		if (!orderData) return false;
		const pay = String(orderData.recvorder_Paymentrecvstatus || '').trim().toLowerCase();
		if (pay !== 'completed') return false;
		const st = orderData.recvorder_status;
		return String(st || '').trim().toLowerCase() === 'completed' || isPartialRefundStatus(st);
	};

	const computeDraftRefundTotal = () => {
		let total = 0;
		orderDetailsList.forEach((d) => {
			if (!isRefundableDetailRow(d)) return;
			const id = getDetailPk(d);
			const cap = lineRefundCap(d);
			const q = Math.min(cap, Math.max(0, Number(refundSelections[id]) || 0));
			if (q <= 0) return;
			const price = getMenuItemPrice(d);
			const lineQty = Number(getMenuItemQty(d)) || 1;
			const disc = Number(d.discountAmount ?? d.DiscountAmount ?? 0);
			const perUnitDisc = lineQty > 0 ? disc / lineQty : 0;
			total += (price - perUnitDisc) * q;
		});
		return Math.round(total * 100) / 100;
	};

	const handleOpenPartialRefund = () => {
		const init = {};
		orderDetailsList.forEach((d) => {
			if (!isRefundableDetailRow(d)) return;
			const id = getDetailPk(d);
			if (id && lineRefundCap(d) > 0) init[id] = '';
		});
		setRefundSelections(init);
		setRefundNotes('');
		setRefundPaymentMethod(inferDefaultRefundPaymentMethod(orderData));
		setPartialRefundOpen(true);
	};

	const handleSubmitPartialRefund = async () => {
		if (!orderData || partialRefundSubmitting) return;
		const compId = orderData.compID ?? orderData.CompID;
		const invoiceNo = orderData.recvorder_InvoiceNo || orderData.Recvorder_InvoiceNo;
		const adjustmentKey =
			typeof crypto !== 'undefined' && crypto.randomUUID
				? crypto.randomUUID()
				: `ref-${Date.now()}-${Math.random().toString(16).slice(2)}`;

		const refundLines = [];
		orderDetailsList.forEach((d) => {
			if (!isRefundableDetailRow(d)) return;
			const detailId = getDetailPk(d);
			if (!detailId) return;
			const cap = lineRefundCap(d);
			const q = Math.min(cap, Math.max(0, Number(refundSelections[detailId]) || 0));
			if (q <= 0) return;
			refundLines.push({
				refundLine_ID: 0,
				recvorder_InvoiceNo: invoiceNo,
				compID: compId,
				recvorderAdjustment_ID: '0',
				recvorderDetail_ID: detailId,
				refundQty: q,
			});
		});

		if (!refundLines.length) {
			showErrorToast('Enter a refund quantity for at least one line.');
			return;
		}

		const amount = computeDraftRefundTotal();
		if (amount <= 0) {
			showErrorToast('Refund amount must be greater than zero.');
			return;
		}

		const paymentMethod = String(refundPaymentMethod ?? '').trim().toLowerCase();
		if (!paymentMethod) {
			showErrorToast('Select how the refund was paid (cash, card, online, or deposit).');
			return;
		}

		const mergedAdjustments = (Array.isArray(adjustmentsList) ? adjustmentsList : []).map((a) => {
			const copy = { ...a };
			delete copy.refundLines;
			delete copy.RefundLines;
			return copy;
		});

		const reasonText = refundNotes?.trim() ? refundNotes.trim() : null;
		mergedAdjustments.push({
			adjustment_ID: adjustmentKey,
			invoiceNo,
			compID: compId,
			type: 'REFUND',
			points: 0,
			amount,
			status: 'FINALIZED',
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
			source_Label: reasonText,
			notes: reasonText,
			paymentMethod,
		});

		const existingRoot = Array.isArray(
			orderData.recvorderRefundLines ?? orderData.RecvorderRefundLines
		)
			? (orderData.recvorderRefundLines ?? orderData.RecvorderRefundLines).map((rl) => ({ ...rl }))
			: [];
		const newLinesTagged = refundLines.map((rl) => ({
			...rl,
			clientAdjustmentKey: adjustmentKey,
		}));

		let payload;
		try {
			payload = JSON.parse(JSON.stringify(orderData));
		} catch {
			showErrorToast('Order payload could not be cloned.');
			return;
		}
		payload.recvorderAdjustments = mergedAdjustments;
		if (payload.RecvorderAdjustments !== undefined) delete payload.RecvorderAdjustments;
		payload.recvorderRefundLines = [...existingRoot, ...newLinesTagged];
		if (payload.RecvorderRefundLines !== undefined) delete payload.RecvorderRefundLines;

		try {
			setPartialRefundSubmitting(true);
			const res = await axiosInstance.post(APIs.UPDATE_RECV_ORDER, payload);
			if (res.data?.status) {
				showSuccessToast(res.data?.message || 'Refund recorded.');
				setPartialRefundOpen(false);
				await fetchOrderDetails();
			} else {
				showErrorToast(res.data?.message || 'Update failed.');
			}
		} catch (e) {
			showErrorToast(e.response?.data?.message || e.message || 'Update failed.');
		} finally {
			setPartialRefundSubmitting(false);
		}
	};

	if (loading) {
		return (
			<div className={`order-detail-view ${isDarkMode ? 'order-detail-view--dark' : ''}`}>
				<div className="order-detail-view__loading" role="status" aria-live="polite">
					<ServeIQLoader size={64} />
					<p className="order-detail-view__state-text mb-0">Loading order details…</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className={`order-detail-view ${isDarkMode ? 'order-detail-view--dark' : ''}`}>
				<div className="order-detail-view__state">
					<h2 className="order-detail-view__state-title">Couldn’t load order</h2>
					<p className="order-detail-view__state-text">{error}</p>
					<Link to="/order/manageOrder" className="order-detail-view__back">
						<i className="fa fa-arrow-left" aria-hidden="true" />
						Back to orders
					</Link>
				</div>
			</div>
		);
	}

	if (!orderData) {
		return (
			<div className={`order-detail-view ${isDarkMode ? 'order-detail-view--dark' : ''}`}>
				<div className="order-detail-view__state">
					<h2 className="order-detail-view__state-title">No order found</h2>
					<p className="order-detail-view__state-text">
						No order data found for the provided invoice number.
					</p>
					<Link to="/order/manageOrder" className="order-detail-view__back">
						<i className="fa fa-arrow-left" aria-hidden="true" />
						Back to orders
					</Link>
				</div>
			</div>
		);
	}

	const paymentStatusRaw = String(orderData?.recvorder_Paymentrecvstatus || '').trim().toLowerCase();
	const isPaid = paymentStatusRaw === 'completed';
	const paymentLabel = isPaid ? 'Paid' : 'Unpaid';
	const orderStatusLabel = String(orderData?.recvorder_status || '').trim() || 'Unknown';
	const frontOrderId = orderData?.recvorder_FrontOrderID || orderData?.recvorder_InvoiceNo || 'Order';

	const getStatusChipTone = (status) => {
		const s = String(status || '').trim().toLowerCase().replace(/[\s_-]/g, '');
		if (s === 'completed') return 'success';
		if (s.includes('refund') || s === 'cancelled' || s === 'rejected') return 'danger';
		if (s.includes('process') || s === 'ready' || s === 'preorder') return 'warning';
		return 'neutral';
	};

	const orderOutcome = getOrderOutcomeInfo(orderData);

	const timelineItems = [
		{
			label: 'Order placed',
			time: orderData?.recvorder_datetime ? formatDateTime(orderData.recvorder_datetime) : null,
		},
		{
			label: 'Acknowledged',
			time: orderData?.recvorder_Ackdatetime
				? formatDateTime(orderData.recvorder_Ackdatetime)
				: null,
		},
		{
			label: 'Accepted at kitchen',
			time: orderData?.recvorder_AcceptAtKitchenDateTime
				? formatDateTime(orderData.recvorder_AcceptAtKitchenDateTime)
				: null,
		},
		{
			label: 'Ready at kitchen',
			time: orderData?.recvorder_ReadyAtKitchenDateTime
				? formatDateTime(orderData.recvorder_ReadyAtKitchenDateTime)
				: null,
		},
		{
			label: 'Last updated',
			time: orderData?.recvorder_UpdateDateTime
				? formatDateTime(orderData.recvorder_UpdateDateTime)
				: null,
		},
		{
			label: 'Completed',
			time: orderData?.recvorder_OrderCompletedDateTime
				? formatDateTime(orderData.recvorder_OrderCompletedDateTime)
				: null,
		},
	].filter((item) => item.time);

	const orderType = String(orderData?.recvorder_Ordertype || orderData?.Recvorder_Ordertype || '')
		.toLowerCase()
		.trim();
	const isDelivery = orderType === 'delivery';
	const isCollectionOrPickup =
		orderType === 'collection' || orderType === 'pickup' || orderType === 'takeaway';
	const isDeliveryOrCollection = isDelivery || isCollectionOrPickup;
	const pickExpectedTime = (...keys) => {
		for (const key of keys) {
			const value = orderData?.[key];
			if (value != null && String(value).trim() !== '') return String(value).trim();
		}
		return '';
	};
	const expectedDateTime = isCollectionOrPickup
		? pickExpectedTime(
				'recvorder_ExpectedCollectionTime',
				'Recvorder_ExpectedCollectionTime',
				'recvorder_ExpectedDeliveryTime',
				'Recvorder_ExpectedDeliveryTime'
			)
		: pickExpectedTime(
				'recvorder_ExpectedDeliveryTime',
				'Recvorder_ExpectedDeliveryTime'
			);
	const expectedTimeLabel = isDelivery
		? 'Expected delivery'
		: 'Expected collection';
	const tableName =
		orderData?.T_Name ||
		orderData?.t_Name ||
		orderData?.tableName ||
		orderData?.TableName ||
		orderData?.recvorder_TableName ||
		null;
	const frontOrderIdUpper = String(orderData?.recvorder_FrontOrderID || '').toUpperCase().trim();
	const receivedFrom = String(
		orderData?.recvorder_ReceivedFrom ||
			orderData?.Recvorder_ReceivedFrom ||
			orderData?.receivedFrom ||
			''
	)
		.toUpperCase()
		.trim();
	const isOnlineOrderingOrder = frontOrderIdUpper.startsWith('ONL-') || receivedFrom === 'WEB';
	const itemCount = getActualItemCount(orderDetailsList) || 0;
	const paymentCharges = getPaymentDisplayCharges(orderDetailsList, orderData);

	return (
		<div className={`order-detail-view ${isDarkMode ? 'order-detail-view--dark' : ''}`}>
			<ol className="breadcrumb mb-2">
				<li className="breadcrumb-item">
					<Link to="/">Home</Link>
				</li>
				<li className="breadcrumb-item">
					<Link to="/order/manageOrder">Orders</Link>
				</li>
				<li className="breadcrumb-item active">Order Details</li>
			</ol>

			<header className="order-detail-view__hero">
				<div>
					<p className="order-detail-view__eyebrow">Order details</p>
					<h1 className="order-detail-view__title">{frontOrderId}</h1>
					<div className="order-detail-view__chips">
						<span
							className={`order-detail-view__chip order-detail-view__chip--${
								isPaid ? 'success' : 'warning'
							}`}
						>
							{paymentLabel}
						</span>
						<span className="order-detail-view__chip">
							{getDisplayPaymentMethod(orderData) || 'N/A'}
						</span>
						<span
							className={`order-detail-view__chip order-detail-view__chip--${getStatusChipTone(
								orderStatusLabel
							)}`}
						>
							{orderStatusLabel}
						</span>
						{orderData?.recvorder_Ordertype ? (
							<span className="order-detail-view__chip">{orderData.recvorder_Ordertype}</span>
						) : null}
					</div>
					<div className="order-detail-view__meta">
						<span>
							Invoice <strong>{orderData?.recvorder_InvoiceNo || 'N/A'}</strong>
						</span>
						<span>
							Date{' '}
							<strong>
								{orderData?.recvorder_datetime
									? formatDateTime(orderData.recvorder_datetime)
									: 'N/A'}
							</strong>
						</span>
						{orderData?.recvorder_ReceivedFrom || orderData?.Recvorder_ReceivedFrom ? (
							<span>
								Source{' '}
								<strong>
									{orderData?.recvorder_ReceivedFrom || orderData?.Recvorder_ReceivedFrom}
								</strong>
							</span>
						) : null}
					</div>
				</div>
			</header>

			<div className="row gx-4">
				<div className="col-xl-8 mb-xl-0 mb-4">
					<section className="order-detail-view__panel od-items-card">
						<div className="order-detail-view__panel-header">
							<div className="order-detail-view__panel-icon">
								<i className="fa fa-shopping-bag" aria-hidden="true" />
							</div>
							<div>
								<h2 className="order-detail-view__panel-title">Items</h2>
								<p className="order-detail-view__panel-subtitle">
									{itemCount} {itemCount === 1 ? 'item' : 'items'}
								</p>
							</div>
						</div>
						<div className="order-detail-view__panel-body">
							{orderDetailsList.length > 0 ? (
								(() => {
									const roots = buildOrderTree(orderDetailsList);
									return roots.map((root, idx) => (
										<div key={root.id ?? idx}>
											{renderOrderNode(root, 0)}
											{idx < roots.length - 1 && <hr className="od-item-divider" />}
										</div>
									));
								})()
							) : (
								<div className="od-empty-items">
									<i className="fa fa-shopping-cart fa-2x mb-2 opacity-50" aria-hidden="true" />
									<p className="mb-0">No items found in this order</p>
								</div>
							)}
						</div>
					</section>

					<section className="order-detail-view__panel">
						<div className="order-detail-view__panel-header">
							<div className="order-detail-view__panel-icon">
								<i className="fa fa-receipt" aria-hidden="true" />
							</div>
							<div>
								<h2 className="order-detail-view__panel-title">Payment summary</h2>
								<p className="order-detail-view__panel-subtitle">Charges and payment records</p>
							</div>
						</div>
						<div className="order-detail-view__panel-body">
							<table className="order-detail-view__charges">
								<tbody>
									<tr>
										<td>Subtotal</td>
										<td>{itemCount} items</td>
										<td>{formatCurrency(paymentCharges.payableSubtotal || 0)}</td>
									</tr>
									{paymentCharges.displayVat > 0 && (
										<tr>
											<td>
												Tax (
												{String(orderData?.recvorder_VATType || '').toUpperCase() === 'INCLUSIVE'
													? 'INCLUSIVE'
													: orderData?.recvorder_VATType}
												)
											</td>
											<td>
												VAT {orderData?.recvorder_VATpercentage || 0}%
												{String(orderData?.recvorder_VATType || '').toUpperCase() === 'INCLUSIVE'
													? ' (included in subtotal)'
													: ''}
											</td>
											<td>{formatCurrency(paymentCharges.displayVat || 0)}</td>
										</tr>
									)}
									{paymentCharges.displayService > 0 && (
										<tr>
											<td>Service charges</td>
											<td>{orderData?.recvorder_Servicechargespercentage?.toFixed(2) || 0}%</td>
											<td>{formatCurrency(paymentCharges.displayService || 0)}</td>
										</tr>
									)}
									{(Number(orderData?.recvorder_ExtraCharges ?? orderData?.Recvorder_ExtraCharges) || 0) >
										0 && (
										<tr>
											<td>Extra charges</td>
											<td>Extra charges</td>
											<td>
												{formatCurrency(
													orderData?.recvorder_ExtraCharges ?? orderData?.Recvorder_ExtraCharges ?? 0
												)}
											</td>
										</tr>
									)}
									{(Number(orderData?.recvorder_Totaldiscount) || 0) > 0 && (
										<tr>
											<td>Discount</td>
											<td>{formatPercentage(orderData?.recvorder_Totaldiscountpercentage)}%</td>
											<td className="od-discount">
												-{formatCurrency(orderData?.recvorder_Totaldiscount || 0)}
											</td>
										</tr>
									)}
									{showPromoCodeRow && (
										<tr>
											<td>Promo code</td>
											<td>
												<span className="od-discount text-decoration-underline">
													{orderData.recvorder_promotioncode}
												</span>
											</td>
											<td className="od-discount">
												-{formatCurrency(orderData?.recvorder_promotionDiscountAmount || 0)}
											</td>
										</tr>
									)}
									{(Number(orderData?.recvorder_TipAmount) || 0) > 0 && (
										<tr>
											<td>Tip</td>
											<td>Customer tip</td>
											<td>{formatCurrency(orderData?.recvorder_TipAmount || 0)}</td>
										</tr>
									)}
									{orderData?.recvorder_Deliverycharges > 0 && (
										<tr>
											<td>Delivery fee</td>
											<td>Delivery charges</td>
											<td>{formatCurrency(orderData?.recvorder_Deliverycharges || 0)}</td>
										</tr>
									)}
									<tr className="od-total">
										<td colSpan="2">Total</td>
										<td>{formatCurrency(orderData?.recvorder_Totalamount || 0)}</td>
									</tr>
									{orderData?.paymentDetails &&
										orderData.paymentDetails.length > 0 &&
										orderData.paymentDetails.map((payment) => (
											<tr key={payment.id}>
												<td>Paid by customer</td>
												<td>
													via {getDisplayPaymentMethod(payment) || payment.paymentmethod}
												</td>
												<td>{formatCurrency(payment.amount)}</td>
											</tr>
										))}
								</tbody>
							</table>
						</div>
						{renderOrderOutcomeBanner(orderOutcome)}
					</section>
				</div>

				<div className="col-xl-4">
					<section className="order-detail-view__panel">
						<div className="order-detail-view__panel-header">
							<div className="order-detail-view__panel-icon">
								<i className="fa fa-sticky-note" aria-hidden="true" />
							</div>
							<div>
								<h2 className="order-detail-view__panel-title">Notes</h2>
							</div>
						</div>
						<div className="order-detail-view__panel-body">
							{orderData?.recvorder_comments ? (
								<p className="order-detail-view__note">
									<strong>Order comments</strong>
									{orderData.recvorder_comments}
								</p>
							) : (
								<p className="order-detail-view__note text-muted mb-0">No notes from customer</p>
							)}
							{orderData?.recorder_Ackcomments && (
								<p className="order-detail-view__note">
									<strong>Acknowledgment comments</strong>
									{orderData.recorder_Ackcomments}
								</p>
							)}
						</div>
					</section>

					<section className="order-detail-view__panel">
						<div className="order-detail-view__panel-header">
							<div className="order-detail-view__panel-icon">
								<i className="fa fa-user" aria-hidden="true" />
							</div>
							<div>
								<h2 className="order-detail-view__panel-title">Customer</h2>
							</div>
						</div>
						<div className="order-detail-view__panel-body">
							<div className="order-detail-view__customer">
								<div className="order-detail-view__avatar" aria-hidden="true">
									<i className="fa fa-user" />
								</div>
								<p className="order-detail-view__customer-name">
									{orderData?.customerName || 'Guest Customer'}
								</p>
							</div>
						</div>
					</section>

					<section className="order-detail-view__panel">
						<div className="order-detail-view__panel-header">
							<div className="order-detail-view__panel-icon">
								<i className="fa fa-info-circle" aria-hidden="true" />
							</div>
							<div>
								<h2 className="order-detail-view__panel-title">Order information</h2>
							</div>
						</div>
						<div className="order-detail-view__panel-body">
							<dl className="order-detail-view__info-list">
								{orderData?.recvorder_FrontOrderID && (
									<div className="order-detail-view__info-row">
										<dt>Front order ID</dt>
										<dd>{orderData.recvorder_FrontOrderID}</dd>
									</div>
								)}
								<div className="order-detail-view__info-row">
									<dt>Order type</dt>
									<dd>{orderData?.recvorder_Ordertype || 'N/A'}</dd>
								</div>
								{expectedDateTime && (isDelivery || isCollectionOrPickup) && (
									<div className="order-detail-view__info-row">
										<dt>{expectedTimeLabel}</dt>
										<dd>{formatDateTime(expectedDateTime)}</dd>
									</div>
								)}
								{orderData?.recvorder_Tableid && !isDeliveryOrCollection && (
									<div className="order-detail-view__info-row">
										<dt>Table</dt>
										<dd>{tableName || orderData.recvorder_Tableid}</dd>
									</div>
								)}
								<div className="order-detail-view__info-row">
									<dt>Payment method</dt>
									<dd>{getDisplayPaymentMethod(orderData) || 'N/A'}</dd>
								</div>
								<div className="order-detail-view__info-row">
									<dt>Payment status</dt>
									<dd>{orderData?.recvorder_Paymentrecvstatus || 'Unknown'}</dd>
								</div>
								{!isOnlineOrderingOrder && (
									<div className="order-detail-view__info-row">
										<dt>Till</dt>
										<dd>{orderData?.tillname || 'N/A'}</dd>
									</div>
								)}
								{Number(orderData?.recvorder_ReadyMins || 0) > 0 && (
									<div className="order-detail-view__info-row">
										<dt>Ready time</dt>
										<dd>{orderData.recvorder_ReadyMins} minutes</dd>
									</div>
								)}
							</dl>
							{orderData?.recvOrder_DeliveryAddress && (
								<div className="mt-3">
									<p className="order-detail-view__panel-subtitle mb-2">Delivery address</p>
									<p className="order-detail-view__address">{orderData.recvOrder_DeliveryAddress}</p>
								</div>
							)}
						</div>
					</section>

					{timelineItems.length > 0 && (
						<section className="order-detail-view__panel">
							<div className="order-detail-view__panel-header">
								<div className="order-detail-view__panel-icon">
									<i className="fa fa-clock" aria-hidden="true" />
								</div>
								<div>
									<h2 className="order-detail-view__panel-title">Order timeline</h2>
								</div>
							</div>
							<div className="order-detail-view__panel-body">
								<ul className="order-detail-view__timeline">
									{timelineItems.map((item) => (
										<li key={item.label} className="order-detail-view__timeline-item">
											<p className="order-detail-view__timeline-label">{item.label}</p>
											<p className="order-detail-view__timeline-time">{item.time}</p>
										</li>
									))}
								</ul>
							</div>
						</section>
					)}

					{Array.isArray(adjustmentsList) &&
						adjustmentsList.some(
							(a) => String(a?.type ?? a?.Type ?? '').toLowerCase() === 'refund'
						) && (
							<section className="order-detail-view__panel">
								<div className="order-detail-view__panel-header">
									<div className="order-detail-view__panel-icon">
										<i className="fa fa-undo" aria-hidden="true" />
									</div>
									<div>
										<h2 className="order-detail-view__panel-title">Refund history</h2>
									</div>
								</div>
								<div className="order-detail-view__panel-body p-2">
									<table className="table table-hover table-sm mb-0 od-refund-table">
										<thead>
											<tr>
												<th>When</th>
												<th className="text-end">Amount</th>
												<th>Via</th>
												<th>Lines</th>
											</tr>
										</thead>
										<tbody>
											{adjustmentsList
												.filter(
													(a) => String(a?.type ?? a?.Type ?? '').toLowerCase() === 'refund'
												)
												.map((a, idx) => {
													const lines = getRefundLinesForAdjustment(
														a,
														rootRefundLinesList,
														orderDetailsList
													);
													const when = a.createdAt ?? a.CreatedAt ?? '';
													const reason =
														cleanRefundReasonText(a?.source_Label ?? a?.Source_Label) ||
														cleanRefundReasonText(a?.notes ?? a?.Notes);
													const viaRaw = a.paymentMethod ?? a.PaymentMethod ?? '';
													const viaLabel = formatRefundPaymentLabel(viaRaw);
													return (
														<tr key={a.adjustment_ID ?? a.Adjustment_ID ?? idx}>
															<td className="small">{when ? formatDateTime(when) : '—'}</td>
															<td className="text-end small">
																{formatCurrency(Number(a.amount ?? a.Amount) || 0)}
															</td>
															<td className="small">{viaLabel}</td>
															<td className="small">
																{lines.length > 0 ? (
																	<ul className="od-refund-lines mb-0 ps-3">
																		{lines.map((line) => (
																			<li key={`${line.detailId}-${line.qty}`}>
																				{line.name}{' '}
																				<span className="text-muted">× {line.qty}</span>
																			</li>
																		))}
																	</ul>
																) : (
																	<span className="text-muted">—</span>
																)}
																{reason ? (
																	<div className="od-refund-reason text-muted mt-1">
																		{reason}
																	</div>
																) : null}
															</td>
														</tr>
													);
												})}
										</tbody>
									</table>
								</div>
							</section>
						)}
				</div>
			</div>

			<CustomModal
				isOpen={partialRefundOpen}
				onClose={() => !partialRefundSubmitting && setPartialRefundOpen(false)}
				title="Record partial refund"
				isCentered
				showButton={false}
				modalSize="modal-lg"
			>
				<p className="small text-muted">
					Enter refund quantity per line (cannot exceed remaining quantity). Existing refund lines and adjustments are re-saved with this refund batch.
				</p>
				<table className="table table-hover table-sm">
					<thead>
						<tr>
							<th>Item</th>
							<th className="text-end">Max</th>
							<th style={{ width: '120px' }}>Refund qty</th>
						</tr>
					</thead>
					<tbody>
						{orderDetailsList.filter(isRefundableDetailRow).map((d) => {
							const id = getDetailPk(d);
							const cap = lineRefundCap(d);
							if (!id || cap <= 0) return null;
							return (
								<tr key={id}>
									<td className="small">{getMenuItemName(d)}</td>
									<td className="text-end small">{cap}</td>
									<td>
										<input
											type="number"
											min={0}
											max={cap}
											step="0.01"
											className="form-control form-control-sm"
											value={refundSelections[id] ?? ''}
											onChange={(e) =>
												setRefundSelections((prev) => ({ ...prev, [id]: e.target.value }))
											}
										/>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
				<div className="mt-2">
					<label className="form-label small mb-1">Refund via (required)</label>
					<select
						className="form-select form-select-sm mb-2"
						value={refundPaymentMethod}
						onChange={(e) => setRefundPaymentMethod(e.target.value)}
					>
						<option value="">Select payment method…</option>
						{REFUND_PAYMENT_OPTIONS.map((o) => (
							<option key={o.value} value={o.value}>{o.label}</option>
						))}
					</select>
					<label className="form-label small mb-1">Reason (optional)</label>
					<input
						type="text"
						className="form-control form-control-sm"
						value={refundNotes}
						onChange={(e) => setRefundNotes(e.target.value)}
						placeholder="e.g. Customer didn't like the food"
					/>
				</div>
				<div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
					<div>
						Estimated refund: <strong>{formatCurrency(computeDraftRefundTotal())}</strong>
					</div>
					<div>
						<button
							type="button"
							className="btn btn-light btn-sm me-2"
							disabled={partialRefundSubmitting}
							onClick={() => setPartialRefundOpen(false)}
						>
							Cancel
						</button>
						<button
							type="button"
							className="btn btn-danger btn-sm"
							disabled={partialRefundSubmitting}
							onClick={handleSubmitPartialRefund}
						>
							{partialRefundSubmitting ? 'Saving…' : 'Submit refund'}
						</button>
					</div>
				</div>
			</CustomModal>
		</div>
	)
}

export default OrderDetailsView;